import * as R from 'ramda';
import React from 'react';
import {createBrowserRouter, Navigate, Outlet, RouteObject, useSearchParams} from 'react-router-dom';

import {HomePage} from '../pages/home/home.page';
import UniversalPage from "../components/universal-page/univarsal-page";
import {NavigationComponent} from '../components/navigation/navigation.component';

import UserProvider from '../models/user/user-provider';
import DocumentTypeProvider from '../models/document-type/document-type-provider';
import ArticlesProvider from "../models/articles/articles-provider";
import {PageEntityT} from "../models/pages/pages-provider";
import ErrorPage from '../pages/error/error.page';
import {RoutesEnum} from '../constants/routes';
import ShutdownsPage from '../pages/shutdowns/shutdowns.page';
import ShutdownsProvider from '../models/shutdowns/shutdowns-provider';
import CalcPage from '../pages/calc/calc.page';
import CalcProvider from '../models/calc/calc-provider';
import Footer from '../components/footer/footer';
import MapPage from '../pages/map/map.page';
import Content from '../components/content/content.component';
import FacilitiesProvider from '../models/facilities/facilities-provider';
import DownloadPage from '../pages/download/download.page';
import {SignInDialog} from '../providers/auth/signin-dialog';
import {SignUpDialog} from '../providers/auth/signup-dialog';
import {PasswordResetDialog} from '../providers/auth/password-reset-dialog';
import {useAuth} from '../providers/auth/auth-provider';
import {CabinetProvider} from '../models/cabinet/cabinet-provider';
import {CabinetMainPage} from '../pages/cabinet/main';
import { ConnectionPage } from '../pages/cabinet/connection';

const RequireAuth = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  return <>{children}</>;
};

const ProvidersLayout = () => {
  const { openSignInModal, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  React.useEffect(() => {
    if (!searchParams.has('signin')) {
      return;
    }
    if (!isAuthenticated) {
      openSignInModal();
    }
    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete('signin');
    setSearchParams(nextParams, { replace: true });
  }, [isAuthenticated, openSignInModal, searchParams, setSearchParams]);

  return (
    <UserProvider>
      <DocumentTypeProvider>
        <ArticlesProvider>
          <SignInDialog />
          <SignUpDialog />
          <PasswordResetDialog />
          <NavigationComponent/>
          <Content>
            <Outlet/>
          </Content>
          <Footer/>
        </ArticlesProvider>
      </DocumentTypeProvider>
    </UserProvider>
  );
};

const getPageElement = (page: PageEntityT): React.ReactNode => {
  switch (page.url) {
    case RoutesEnum.SHUTDOWNS: {
      return <ShutdownsProvider><ShutdownsPage/></ShutdownsProvider>;
    }
    case RoutesEnum.CALC: {
      return <CalcProvider><CalcPage/></CalcProvider>;
    }
    case RoutesEnum.MAP: {
      return <FacilitiesProvider><MapPage /></FacilitiesProvider>;
    }
  }
  return (page.hasArticle || page.hasDocuments) ? <UniversalPage entity={page}/> : null;
}

const mapPageToRoute = (page: PageEntityT, list: PageEntityT[]): RouteObject => ({
  path: page.url,
  element: getPageElement(page),
  children: list
    .filter((itm) => itm.parentId === page.id)
    .map((itm) => mapPageToRoute(itm, list)),
});

const router = (pages: PageEntityT[]) => {
  const tree = (pages ?? [])
    .filter((page) => R.isNil(page.parentId))
    .map((page) => mapPageToRoute(page, pages));

  return createBrowserRouter([
    {
      element: <ProvidersLayout/>,
      children: [
        {
          path: '/',
          element: <HomePage/>,
          errorElement: <ErrorPage/>,
        },
        {
          path: 'download/*',
          element: <DownloadPage/>,
          errorElement: <ErrorPage/>,
        },
        {
          path: RoutesEnum.CABINET,
          element: (
            <RequireAuth>
              <CabinetProvider>
                <Outlet />
              </CabinetProvider>
            </RequireAuth>
          ),
          children: [
            {
              index: true,
              element: <CabinetMainPage/>,
            },
            {
              path: RoutesEnum.CONNECTION,
              element: <Navigate to={`${RoutesEnum.CONNECTION}/new`} replace />,
            },
            {
              path: `${RoutesEnum.CONNECTION}/:id`,
              element: <ConnectionPage/>,
            }
          ],
          errorElement: <ErrorPage/>,
        },
        ...tree,
      ],
      errorElement: <ErrorPage/>,
    },
  ]);
};

export default router;
